// AcademicDetails.jsx

import { useEffect, useState } from "react";
import { SweetAlertShow } from "../../../master/Master";
import { studentDetailsAcademicDetails } from "../../../../api/StudentDetails";
import { formatToDate } from "../../../master/FormatToDateTime";

const Skeleton = ({ w = "100%", h = "14px" }) => (
  <span
    className="placeholder"
    style={{
      width: w,
      height: h,
      display: "inline-block",
      borderRadius: "6px",
    }}
  ></span>
);

const SectionShell = ({ icon, color, title, empty, emptyText, children }) => (
  <div
    className="p-3 rounded-3 h-100"
    style={{ background: "#f8f9fb", border: "1px solid #eef0f4" }}
  >
    <div className="d-flex align-items-center gap-2 mb-3">
      <div
        className={`d-flex align-items-center justify-content-center rounded-3 bg-${color} bg-opacity-10`}
        style={{ width: 34, height: 34 }}
      >
        <i className={`${icon}  text-${color==='primary'?'white':color}`}></i>
      </div>
      <span className="fw-semibold">{title}</span>
    </div>

    {empty ? (
      <div className="text-center py-3 text-muted">
        <i className="ri-inbox-line d-block fs-4 mb-1"></i>
        <small>{emptyText || `No ${title.toLowerCase()} available`}</small>
      </div>
    ) : (
      children
    )}
  </div>
);

const RowList = ({ rows, color }) => (
  <div className="d-flex flex-column">
    {rows.map((r, i) => (
      <div
        key={i}
        className="d-flex justify-content-between align-items-center py-2"
        style={{
          borderBottom: i !== rows.length - 1 ? "1px dashed #e5e7eb" : "none",
        }}
      >
        <small className="text-secondary d-flex align-items-center gap-2">
          {r.icon && <i className={`${r.icon} text-${color}`}></i>}
          {r.label}
        </small>
        <small className="fw-medium text-dark text-end">{r.value ?? "-"}</small>
      </div>
    ))}
  </div>
);

const parseSubjects = (raw) => {
  if (!raw) return [];
  try {
    const outer = typeof raw === "string" ? JSON.parse(raw) : raw;
    return outer.map((s) => (typeof s === "string" ? JSON.parse(s) : s));
  } catch {
    return [];
  }
};


const subjectColors = ["success", "info", "warning", "danger", "secondary"];

// Maps batch status code -> label, color, icon
const getStatusMeta = (status) => {
  switch (String(status)) {
    case "1":
      return { label: "Active", color: "success", icon: "ri-play-circle-fill" };
    case "5":
      return {
        label: "Complete",
        color: "info",
        icon: "ri-checkbox-circle-fill",
      };
    default:
      return {
        label: "Inactive",
        color: "danger",
        icon: "ri-close-circle-fill",
      };
  }
};

export default function AcademicDetails({ id }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const getStudentDetailsMain = async (student_id) => {
    setLoading(true);
    try {
      const response = await studentDetailsAcademicDetails(student_id);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentDetailsMain(id);
  }, [id]);

  const batches = data?.student_batch_n_fees || [];

  // Active batches first, then most recently created
  const sortedBatches = [...batches].sort((a, b) => {
    const aActive = String(a.status) === "1";
    const bActive = String(b.status) === "1";
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  if (loading) {
    return (
      <div className="d-flex flex-column gap-3">
        {[1, 2].map((n) => (
          <div
            key={n}
            className="p-4 bg-white rounded-3"
            style={{ border: "1px solid #eef0f4" }}
          >
            <div className="placeholder-glow mb-3">
              <Skeleton w="220px" h="22px" />
            </div>
            <div className="row g-3">
              {[1, 2, 3, 4].map((c) => (
                <div className="col-lg-3" key={c}>
                  <div className="placeholder-glow d-flex flex-column gap-2">
                    <Skeleton h="16px" />
                    <Skeleton h="16px" />
                    <Skeleton h="16px" />
                    <Skeleton h="16px" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!sortedBatches.length) {
    return (
      <div
        className="p-5 bg-white rounded-3 text-center text-muted"
        style={{ border: "1px solid #eef0f4" }}
      >
        <i className="ri-graduation-cap-line d-block fs-1 mb-2"></i>
        <p className="mb-0">No academic details available</p>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {sortedBatches.map((item, idx) => {
        const meta = getStatusMeta(item?.status);
        const subjects = parseSubjects(item?.subjects);

        return (
          <div
            key={item.id}
            className="bg-white rounded-3 overflow-hidden"
            style={{ border: "1px solid #eef0f4" }}
          >
            {/* Batch Header */}
            <div
              className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3"
              style={{
                background:
                  meta.color === "success"
                    ? "linear-gradient(135deg, #eaf6ef, #f2fbf5)"
                    : "linear-gradient(135deg, #f4f4f6, #f9f9fa)",
                borderBottom: "1px solid #eef0f4",
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle bg-${meta.color} bg-opacity-10`}
                  style={{ width: 44, height: 44 }}
                >
                  <i className={`${meta.icon} text-${meta.color} fs-4`}></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h6 className="mb-0 fw-bold">
                      {item?.course?.title || "-"}
                    </h6>
                    <span className={`badge rounded-pill bg-${meta.color}`}>
                      <i
                        className="ri-checkbox-blank-circle-fill me-1"
                        style={{ fontSize: 6 }}
                      ></i>
                      {meta.label}
                    </span>
                  </div>
                  <small className="text-secondary d-flex align-items-center gap-1 mt-1 flex-wrap">
                    <i className="ri-team-line"></i> {item?.batch?.title || "-"}
                    <span className="mx-1">•</span>
                    <i className="ri-calendar-line"></i>{" "}
                    {item?.session_year?.session || "-"}
                  </small>
                  {item?.reason && (
                    <small className="text-secondary d-flex align-items-center gap-1 mt-1">
                      <i className="ri-message-2-line"></i> {item.reason}
                    </small>
                  )}
                </div>
              </div>

              <div className="text-end">
                <small className="text-secondary d-block">
                  <i className="ri-calendar-event-line me-1"></i>
                  {formatToDate(item?.batch_enroll_date)}
                  {item?.batch_end_date
                    ? ` — ${formatToDate(item.batch_end_date)}`
                    : " - Present"}
                </small>
              </div>
            </div>

            {/* Body */}
            <div className="p-3">
              <div className="row g-3">
                {/* Academic Details */}
                <div className="col-lg-4">
                  <SectionShell
                    icon="ri-graduation-cap-line"
                    color="primary"
                    title="Academic"
                  >
                    <RowList
                      color="primary"
                      rows={[
                        {
                          icon: "ri-hashtag",
                          label: "Roll No",
                          value: item?.roll_number,
                        },
                        {
                          icon: "ri-id-card-line",
                          label: "Reg No",
                          value: data?.registration_no,
                        },
                        {
                          icon: "ri-shield-check-line",
                          label: "RTE Status",
                          value: item?.rte_status,
                        },
                        {
                          icon: meta.icon,
                          label: "Status",
                          value: (
                            <span
                              className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color}`}
                            >
                              {meta.label}
                            </span>
                          ),
                        },
                      ]}
                    />
                  </SectionShell>
                </div>

                {/* Class Information */}
                <div className="col-lg-4">
                  <SectionShell
                    icon="ri-school-line"
                    color="info"
                    title="Class Info"
                  >
                    <RowList
                      color="info"
                      rows={[
                        {
                          icon: "ri-user-star-line",
                          label: "Counselor",
                          value: item?.counselor?.name,
                        },
                        {
                          icon: "ri-phone-line",
                          label: "Mobile",
                          value: item?.counselor?.mobile,
                        },
                        {
                          icon: "ri-time-line",
                          label: "Timing",
                          value:
                            item?.batch?.start_time && item?.batch?.end_time
                              ? `${item.batch.start_time} - ${item.batch.end_time}`
                              : "-",
                        },
                        {
                          icon: "ri-hourglass-line",
                          label: "Duration",
                          value: item?.batch?.duration
                            ? `${item.batch.duration} mo`
                            : "-",
                        },
                      ]}
                    />
                  </SectionShell>
                </div>

                {/* Subjects */}
                <div className="col-lg-4">
                  <SectionShell
                    icon="ri-book-open-line"
                    color="warning"
                    title="Subjects"
                    empty={!subjects.length}
                  >
                    <div className="d-flex flex-wrap gap-2">
                      {subjects.map((s, i) => (
                        <span
                          key={s?.id || i}
                          className={`badge bg-${subjectColors[i % subjectColors.length]} bg-opacity-10 text-${subjectColors[i % subjectColors.length]} d-flex align-items-center gap-1 px-2 py-2`}
                        >
                          <i className="ri-bookmark-line"></i>
                          {s?.name}
                        </span>
                      ))}
                    </div>
                  </SectionShell>
                </div>
              </div>
            </div>

            {idx !== sortedBatches.length - 1 && (
              <div
                className="d-flex justify-content-center py-1"
                style={{ background: "#fafafa" }}
              >
                <i className="ri-arrow-down-line text-muted"></i>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
